import { observer } from "mobx-react-lite";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { PlaygroundStoreContext } from "@/components/Playground2/playground-store";
import { useContext } from "react";
import Functions from "../PromptBlueprint/components/Functions";
import { ParametersPreviewContent } from "./AdvancedParameters/ParametersPreview";
import { ParametersContent } from "./ParametersContent/ParametersContent";
import { ParametersTrigger } from "./ParametersTrigger";
import { ParameterProvider } from "./store";
import { ModelParametersProps } from "./types";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useModels } from "@/queries/models";
import { useParams } from "react-router-dom";
import { useAuth } from "@/context/auth-context";
import { useInferenceClientsList } from "@/queries";

export const ModelParameters = observer(
  ({
    promptData,
    className,
    onUpdate,
    isPromptChat,
    displayAsPopover = true,
    shouldShowClearButton = true,
    readonly = false,
    showLearnMore = true,
    titleClassName,
    triggerLabel,
    triggerLogo,
    triggerVariant = "ghost",
    buttonSize = "sm",
  }: ModelParametersProps) => {
    const auth = useAuth();
    const { workspaceId } = useParams();
    const playground = useContext(PlaygroundStoreContext);
    const { data: providersWithModels, isLoading: isModelsLoading } = useModels(
      auth?.userToken || "",
      workspaceId || "",
    );
    const { data: inferenceClientData, isLoading: isInferenceClientsLoading } =
      useInferenceClientsList(auth?.userToken || "", workspaceId || "");

    if (isModelsLoading || !promptData || isInferenceClientsLoading) {
      return (
        <div className="flex h-full w-[132px] items-center justify-center">
          <LoadingSpinner size={4} />
        </div>
      );
    }

    return (
      <ParameterProvider
        providersWithModels={providersWithModels}
        inferenceClientData={inferenceClientData?.inference_clients}
        promptData={promptData}
        isPromptChat={isPromptChat}
        onUpdate={onUpdate}
      >
        <div className={`flex ${className} gap-2`}>
          {displayAsPopover ? (
            <Popover>
              <PopoverTrigger asChild>
                <ParametersTrigger
                  triggerLabel={triggerLabel}
                  triggerLogo={triggerLogo}
                  triggerVariant={triggerVariant}
                  buttonSize={buttonSize}
                />
              </PopoverTrigger>
              <PopoverContent
                className={
                  readonly
                    ? "w-[450px] overflow-visible p-4"
                    : "w-full max-w-xl"
                }
              >
                {readonly ? (
                  <ParametersPreviewContent
                    promptData={promptData || null}
                    isChat={isPromptChat}
                    showLearnMore={showLearnMore}
                    titleClassName={titleClassName}
                  />
                ) : (
                  <ParametersContent
                    shouldShowClearButton={shouldShowClearButton}
                  />
                )}
              </PopoverContent>
            </Popover>
          ) : readonly ? (
            <ParametersPreviewContent
              promptData={promptData || null}
              isChat={isPromptChat}
              showLearnMore={showLearnMore}
              titleClassName={titleClassName}
            />
          ) : (
            <ParametersContent shouldShowClearButton={shouldShowClearButton} />
          )}
          {playground && <Functions readonly={readonly} />}
        </div>
      </ParameterProvider>
    );
  },
);
